import { LISTING_TYPES } from "repoV2/features/Listing/Listing.constants";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import {
    IClassDetails,
    IClassDetailsInApi,
    ISessionDetailsObjInApiResponse,
    ISlotDetails,
    ISlotDetailsInApi,
} from "repoV2/features/CheckoutPages/utils/apis/SessionDetailsApi/SessionDetailsApi.interface";
import { isPast, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { getUserTimezone } from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { IProcessSessionDetailsArgs } from "../SessionDetailsFormGroup.interfaces";

/**
 * @dev This function processes the slot Details from api
 * (session details used for webinar,appointments and workshops)
 * into a reformed/consumable slot Details object based on user timezone
 * used in `processSessionDetails` in
 * `repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/SessionDetailsFormGroup.utils.ts`
 * @param slots
 * @returns slotDetails object
 * {
 *    uuid => slot uuid,
 *    startDate => slot start dateTime string with timezone offset in utc format,
 *    startTime => slot start Time string with timezone offset in utc format,
 *    endTime => slot end Time string with timezone offset in utc format,
 *    spotsFilled => no. of spots filled/booked for that slot,
 *  }
 */
const processSlotsDetails = (slots?: ISlotDetailsInApi[]): ISlotDetails[] => {
    const timezone = getUserTimezone();
    return (slots || []).map(_slot => {
        const slot = _slot || {};

        const parseDate = parseISO(slot.start_time);
        const isPastDate = isPast(parseDate);

        const startDate = formatInTimeZone(
            new Date(slot.start_time),
            timezone,
            "EEEE, dd MMMM yyyy"
        );
        const startTime = formatInTimeZone(
            new Date(slot.start_time),
            timezone,
            "hh:mm a"
        );
        const endTime = formatInTimeZone(
            new Date(slot.end_time),
            timezone,
            "hh:mm a"
        );
        return {
            uuid: slot.uuid,
            startDate,
            startTime,
            endTime,
            spotsFilled: slot.spots_filled,
            isPast: isPastDate,
        };
    });
};

/**
 * @dev This function processes the class Details from api
 * (session details used for one-on-one/group/rolling classes)
 * into a reformed/consumable class Details object
 * used in `processSessionDetails` in
 * `repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/SessionDetailsFormGroup.utils.ts`
 * @param classes
 * @returns classDetails object
 * {
 *    uuid => class uuid,
 *    timings => class timings string,
 *    slotsFilled => no. of slots filled/booked for that class,
 *  }
 */
const processClassesDetails = (
    classes?: IClassDetailsInApi[]
): IClassDetails[] => {
    return (classes || []).map(_cls => {
        const cls = _cls || {};

        return {
            uuid: cls.uuid,
            timings: cls.timings,
            timings_list: cls.timings_list,
            schedult_type: cls.schedule_type,
            starting_date: cls.starting_date,
            slotsFilled: cls.slots_filled,
        };
    });
};

/**
 * @dev this function processes classes/slots from api into the uniform Session Details object
 * Used in `getCheckoutPageSessionDetails` of `useFetchNSetCheckoutPageSessionsSection`
 * here `repoV2/features/CheckoutPages/utils/useFetchNSetCheckoutPageSessionsSection.ts`
 * @param arg0 `{slots, classes, listingType}`
 * @param_summary
 * slots => session details for webinars/appointments/workshops
 * classes => session details for one-on-one/group/rolling classes
 * @returns `sessionDetails` =>
 * { slots: slots based on listing Type, classes: classes based on listing Type}
 */
const processSessionDetails = ({
    slots,
    classes,
    listingType,
}: IProcessSessionDetailsArgs) => {
    let processedSlots: ISlotDetails[] = [];
    let processedClasses: IClassDetails[] = [];
    switch (listingType) {
        case LISTING_TYPES.APPOINTMENTS:
        case LISTING_TYPES.WORKSHOPS:
        case LISTING_TYPES.WEBINAR:
            processedSlots = processSlotsDetails(slots);
            break;
        case LISTING_TYPES.GROUP_CLASSES:
        case LISTING_TYPES.ROLLING_CLASSES:
        case LISTING_TYPES.ONE_ON_ONE_CLASSES:
            processedClasses = processClassesDetails(classes);
            break;
        default:
            processedClasses = processClassesDetails(classes);
            break;
    }
    return {
        slots: processedSlots,
        classes: processedClasses,
    };
};

/**
 * @param {boolean} param0.showExpressInterest backend key for `is_express_interest`.
 * @returns
 */
export const processCallSessionDetailsApiResponse = ({
    showExpressInterest,
    data = {},
    listingType,
}: {
    showExpressInterest?: boolean;
    data?: ISessionDetailsObjInApiResponse;
    listingType?: number;
}) => {
    let showExpressInterestForm = showExpressInterest;

    // In case of empty slots or classes, we change the
    // 'showExpressInterestForm' state as true
    switch (listingType) {
        case LISTING_TYPES.WORKSHOPS:
        case LISTING_TYPES.WEBINAR:
            if (checkIsArrayEmpty(data.slots || [])) {
                showExpressInterestForm = true;
            }
            break;
        case LISTING_TYPES.APPOINTMENTS: {
            break;
        }
        default:
            break;
    }

    const sessionDetails: {
        slots: ISlotDetails[];
        classes: IClassDetails[];
        startDate?: string;
        endDate?: string;
        maxSpots?: number;
    } = processSessionDetails({
        slots: data.slots,
        classes: data.classes,
        listingType,
    });

    if (data?.start_date) {
        sessionDetails.startDate = data.start_date;
    }

    if (data?.end_date) {
        sessionDetails.endDate = data.end_date;
    }

    if (data?.max_spots) {
        sessionDetails.maxSpots = data.max_spots;
    }
    return {
        sessionDetails,
        showExpressInterestForm,
    };
};
